/**
 * Mirror of `artifacts/api-server/src/lib/timezones.ts` - the curated catalog of
 * IANA timezones an admin can assign to a customer, plus small formatting
 * helpers used wherever the UI needs to display a timestamp in the customer's
 * local time.
 *
 * Keep this list in sync with the backend file.
 */

export interface TimezoneOption {
  id: string;
  label: string;
  abbr: string;
}

export const SUPPORTED_TIMEZONES: TimezoneOption[] = [
  { id: "UTC",                 label: "UTC (Coordinated Universal Time)",        abbr: "UTC" },
  { id: "Asia/Singapore",      label: "Singapore (SGT, UTC+8)",                  abbr: "SGT" },
  { id: "Asia/Kuala_Lumpur",   label: "Malaysia (MYT, UTC+8)",                   abbr: "MYT" },
  { id: "Asia/Bangkok",        label: "Thailand (ICT, UTC+7)",                   abbr: "ICT" },
  { id: "Asia/Jakarta",        label: "Indonesia – West (WIB, UTC+7)",           abbr: "WIB" },
  { id: "Asia/Makassar",       label: "Indonesia – Central (WITA, UTC+8)",       abbr: "WITA" },
  { id: "Asia/Jayapura",       label: "Indonesia – East (WIT, UTC+9)",           abbr: "WIT" },
  { id: "Asia/Tokyo",          label: "Japan (JST, UTC+9)",                      abbr: "JST" },
  { id: "Asia/Hong_Kong",      label: "Hong Kong (HKT, UTC+8)",                  abbr: "HKT" },
  { id: "Asia/Manila",         label: "Philippines (PHT, UTC+8)",                abbr: "PHT" },
  { id: "Asia/Dubai",          label: "United Arab Emirates (GST, UTC+4)",       abbr: "GST" },
  { id: "Australia/Sydney",    label: "Australia – Sydney (AEST/AEDT)",          abbr: "AEST" },
  { id: "Europe/London",       label: "United Kingdom (GMT/BST)",                abbr: "GMT" },
  { id: "America/New_York",    label: "US Eastern (EST/EDT)",                    abbr: "EST" },
  { id: "America/Los_Angeles", label: "US Pacific (PST/PDT)",                    abbr: "PST" },
];

const TZ_BY_ID = new Map(SUPPORTED_TIMEZONES.map((t) => [t.id, t]));

/** Returns the catalog entry for a TZ id, or the UTC fallback. */
export function timezoneOption(tz: string | undefined): TimezoneOption {
  if (tz && TZ_BY_ID.has(tz)) return TZ_BY_ID.get(tz)!;
  return SUPPORTED_TIMEZONES[0]!;
}

/** Returns "MYT" / "SGT" / "UTC" etc. for badges. */
export function timezoneAbbr(tz: string | undefined): string {
  return timezoneOption(tz).abbr;
}

/** "YYYY-MM-DD" - the calendar date of `at` in timezone `tz`. */
export function localDateString(at: Date, tz: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(at);
}

/** Calendar-arithmetic shift (TZ-agnostic) on a "YYYY-MM-DD" string. */
export function shiftLocalDate(yyyyMmDd: string, days: number): string {
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  const utc = new Date(Date.UTC(y!, m! - 1, d!));
  utc.setUTCDate(utc.getUTCDate() + days);
  return utc.toISOString().slice(0, 10);
}

/** Yesterday's local calendar date in the customer's timezone. */
export function yesterdayLocalDate(tz: string): string {
  return shiftLocalDate(localDateString(new Date(), tz), -1);
}

/**
 * Format an ISO timestamp / Date in the customer's TZ. Pass any options the
 * native Intl API accepts; `timeZone` is forced to `tz`.
 */
export function formatInTz(
  value: string | Date | number,
  tz: string,
  opts: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" },
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: tz }).format(d);
}
